import { useTranslation } from 'react-i18next'

import {
  type BadgeProps,
  LightModeSemanticColors,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { Badge } from '~/components/common/Badge'
import { PlanSubmitErrorIndicator } from '~/components/common/PlanSubmitErrorIndicator'
import { iconSize } from '~/constants/iconSize'
import type { PlanRowErrorDetailInput } from '~/models/planSubmission/errorDetail'
import type { StatusBadgeVariant } from '~/models/planSubmission/statusBadge'

type Props = {
  variant: StatusBadgeVariant
  hasWarning: boolean
  /** true で variant の色の代わりに neutral で表示する (ラベルは variant のまま)。 */
  muted?: boolean
  /** variant → i18n key の写像。文言の単一 source は i18n locale (page.json) 側。 */
  labelKeys: Record<StatusBadgeVariant, string>
  /**
   * 警告アイコンの hover tooltip / click popover に表示するエラー詳細
   * (plansubmit-error-detail design AC-PLC-008 / 008-1 / 033-1 / 033-2)。
   * 省略時は静的な警告アイコンのみ表示する (= 本 slice 対象外の発電販売計画
   * 画面は BFF field 未配線のため省略し、既存の非対話アイコン表示を維持する
   * — Decision #1 の後方互換)。
   */
  errorDetail?: PlanRowErrorDetailInput
}

/**
 * 計画提出ステータス variant → DS `Badge` の semantic status。
 *
 * 写像は design `design/css/components.css` の計画提出ステータス 6 区分
 * (`.status-*`) に従う (= design が UI の正本)。 design に無い後付けの
 * `generating` / `submitting` は「進行中」 として `info`。
 *
 * design が持つ negative の 2 段階 (差戻し = negative-low / 提出失敗 = negative-mid) と
 * 枠線は DS `Badge` の `status` 一本では表現できないため落とす。 3 つの negative variant
 * は文言 (差戻し / 提出失敗 / 作成失敗) で区別される。
 */
/**
 * 警告アイコン (⚠) の色を badge の semantic status に合わせる。
 *
 * デザイナー指摘 2026-09-14:「黄色い badge の場合は、⚠️ アイコンも黄色に揃えたい」。
 * 以前は negative 固定だったが、 warning (不整合 / 承認待ち) にも ⚠ を出すように
 * なったため、 **黄色い badge に赤いアイコン**が乗る状態になっていた。
 *
 * warning の文字・文中アイコンは `Text.warningHigh` (#5d4e00 / 8.22:1)。
 * 塗り用の `Object.warningMid` (#f3ca00) は白背景に 1.58:1 しか無く AA 未達なので
 * **アイコンには使わない** (`AdminApprovalFlow/constants/eclColor.ts` の同趣旨の注記、
 * および design の `.apr-abcs-warn` が `--text-warning-high` を継承しているのと同じ)。
 */
export function statusBadgeWarnIconColor(variant: StatusBadgeVariant): string {
  return STATUS_BADGE_STATUSES[variant] === 'warning'
    ? LightModeSemanticColors.Text.warningHigh.value
    : LightModeSemanticColors.Text.negativeHigh.value
}

export const STATUS_BADGE_STATUSES: Record<
  StatusBadgeVariant,
  NonNullable<BadgeProps['status']>
> = {
  unsubmitted: 'neutral',
  generating: 'info',
  creationFailed: 'negative',
  rejected: 'negative',
  submitted: 'info',
  accepted: 'positive',
  inconsistent: 'warning',
  submitting: 'info',
  approvalPending: 'warning',
  submitFailed: 'negative',
  sendFailed: 'negative',
}

export const PlanSubmissionStatusBadge = ({
  variant,
  hasWarning,
  muted = false,
  labelKeys,
  errorDetail,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {/* 警告アイコンは Badge の**左**。 design `css/components.css:415-431`
          `.status-warn-icon` が「DS の filled warning triangle を Badge の『左』に
          置き、 状態ラベルより先にエラーの存在が目に入るようにする」 と規定している。
          実装は右に置いており design と逆だった (2026-09-14 デザイナー指摘)。
          対象は hasWarning が立つ 4 variant (作成失敗 / 差戻し(OCCTO) / 提出失敗 /
          送信失敗)。 DOM 順は index.test.tsx が固定する。 */}
      {errorDetail !== undefined && (
        // アイコンの表示可否は PlanSubmitErrorIndicator 自身が hasWarning のみで
        // 決める (review fix #1: hasWarning && errorDetail !== undefined の
        // 2 条件分岐だと messages が空の場合にアイコンごと消えてしまう退行が
        // あった)。 色は badge の status に合わせるため呼び出し側から渡す
        // (Indicator 側は variant を知らない)。
        <PlanSubmitErrorIndicator
          errorDetail={errorDetail}
          hasWarning={hasWarning}
          iconColor={statusBadgeWarnIconColor(variant)}
          variant={variant}
        />
      )}
      {hasWarning && errorDetail === undefined && (
        // errorDetail 未供給 (= 本 slice 対象外の画面) の後方互換フォールバック。
        // 色は badge の status に追随する (negative = 赤 / warning = 黄)。
        <FaTriangleExclamationSolid
          boxSize={iconSize.gridInline}
          color={statusBadgeWarnIconColor(variant)}
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        />
      )}
      <Badge status={muted ? 'neutral' : STATUS_BADGE_STATUSES[variant]}>
        {t(labelKeys[variant])}
      </Badge>
    </span>
  )
}
